import React, { useCallback, useMemo, useState } from 'react';
import { Checkbox, Collapse, Input, Tooltip } from 'antd';
import { AssetItemDto, AWAssetDto, MemberUserDto } from '@api/Api';
import { useOrganization } from '@components/OrganizationBoundary';
import { useDebounced } from '@helpers/useDebounced';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import LottieComponent from '@components/Lottie';
import { useTypedSelector } from '@hooks';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';

import { formattedAWAsset } from '@components/Modals/ApprovalModal/ApprovalModal/utils';
import AssetRow from '@components/Modals/ApprovalModal/ApprovalModal/components/MediaStep/components/AssetRow';

import './AllAssets.less';

type AllAssetsProps = {
  companyName: string;
  assetList: AssetItemDto[];
  approvalWorkflowAssets?: AWAssetDto[];
  selectedAssetVersions: AWAssetDto[];
  setSelectedAssetVersions: (value: AWAssetDto[]) => void;
};

type AssetGroupItem = {
  modifiedAt: string;
  createdAt: string;
  user?: MemberUserDto | null;
  approvalWorkflowId: string | null;
  asset: AWAssetDto;
};

export default function AllAssets({
  companyName,
  assetList,
  selectedAssetVersions,
  approvalWorkflowAssets,
  setSelectedAssetVersions
}: AllAssetsProps) {
  const modalAssetVersions = useTypedSelector(({ modal }) => {
    if (!modal.approvalModal) return [];
    if ('assetVersions' in modal.approvalModal)
      return modal.approvalModal.assetVersions || [];
    if ('approvalWorkflowSplit' in modal.approvalModal)
      return modal.approvalModal.approvalWorkflowSplit.assets || [];
    return [];
  });
  const { currentOrganization } = useOrganization();
  const [assetSearchText, setAssetSearchText] = useState('');
  const searchText = useDebounced(assetSearchText, 250);
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals;
  const canSelectAsset =
    canUseBatchApprovals || selectedAssetVersions.length === 0;

  const { folders, assets } = useMemo(() => {
    let _assetList = assetList;

    if (searchText) {
      _assetList = assetList.filter((item) =>
        item.asset?.versions.some((version) =>
          version.name
            .toLocaleLowerCase()
            .includes(searchText.toLocaleLowerCase())
        )
      );
    }

    return _assetList.reduce(
      (acc, item) => {
        const key = item.path?.[0]?.name;
        const version = item.asset?.versions?.[0];

        if (!version) return acc;

        const assetVersion: AssetGroupItem = {
          modifiedAt: item.modifiedAt,
          createdAt: item.createdAt,
          user: version.user,
          approvalWorkflowId: item.asset?.approvalWorkflow?.id || null,
          asset: formattedAWAsset({
            ...version,
            assetId: item.id
          })
        };

        if (key) {
          if (!acc.folders[key]) acc.folders[key] = [];
          acc.folders[key].push(assetVersion);
        } else {
          acc.assets.push(assetVersion);
        }
        return acc;
      },
      { folders: {}, assets: [] } as {
        folders: {
          [key: string]: AssetGroupItem[];
        };
        assets: AssetGroupItem[];
      }
    );
  }, [assetList, searchText]);

  const availableAssets = useMemo(() => {
    return assetList.filter((item) => {
      const version = item.asset?.versions[0];
      const hasInCurrentEditableAw = approvalWorkflowAssets?.some(
        (asset) => asset.id === item.id
      );
      return (
        !!hasInCurrentEditableAw || (!item.asset?.approvalWorkflow && !!version)
      );
    });
  }, [assetList, approvalWorkflowAssets]);

  const isAllSelectedAssets =
    availableAssets.length === selectedAssetVersions.length;
  const foldersArr = Object.entries(folders);
  const isEmpty = !foldersArr.length && !assets.length;

  const defaultOpenFolders = useMemo(() => {
    return foldersArr
      .map(([name, assets], index) => {
        const hasSelectedAsset = assets.some(({ asset }) =>
          selectedAssetVersions.some(({ id }) => id === asset.id)
        );
        return hasSelectedAsset ? `${name}-${index}` : null;
      })
      .filter(Boolean) as string[];
  }, [foldersArr, selectedAssetVersions]);

  const onSelectFolder = (folderAssets: AssetGroupItem[]) => {
    const folderAssetVersions: AWAssetDto[] =
      folderAssets
        .filter((item) => {
          return !item.approvalWorkflowId;
        })
        .map((item) => item.asset) || [];
    const uniqueAssetVersions = [
      ...folderAssetVersions,
      ...selectedAssetVersions
    ].reduce((acc, curr) => {
      if (!acc.some((item) => item.id === curr.id)) {
        acc.push(curr);
      }
      return acc;
    }, [] as AWAssetDto[]);
    setSelectedAssetVersions(uniqueAssetVersions);
  };

  const onDeleteSelectFolder = useCallback(
    (folderAssets: AssetGroupItem[]) => {
      const folderAssetVersions: AWAssetDto[] =
        folderAssets.map((item) => item.asset) || [];
      const updatedAssetVersions = selectedAssetVersions.filter(
        (selectedVersion) =>
          !folderAssetVersions.some(
            (folderVersion) => folderVersion.id === selectedVersion.id
          )
      );
      setSelectedAssetVersions(updatedAssetVersions);
    },
    [selectedAssetVersions, setSelectedAssetVersions]
  );

  const onToggleSelectAsset = (asset: AWAssetDto, isSelected: boolean) => {
    if (isSelected) {
      setSelectedAssetVersions(
        selectedAssetVersions.filter((i) => i.id !== asset.id)
      );
      return;
    }
    setSelectedAssetVersions([...selectedAssetVersions, asset]);
  };

  const onToggleSelectAllAssets = () => {
    if (!canUseBatchApprovals) return;
    if (isAllSelectedAssets) {
      setSelectedAssetVersions([]);
      return;
    }
    const assets = availableAssets.map((item) => {
      const version = item.asset!.versions[0];
      return formattedAWAsset({
        ...version,
        assetId: item.id
      });
    });
    setSelectedAssetVersions(assets);
  };

  const renderAssetRows = (assets: AssetGroupItem[]) => {
    if (!assets?.length) return null;
    return assets.map((item) => {
      const isSelected = selectedAssetVersions.some(
        (assetVersion) => assetVersion.id === item.asset.id
      );
      const hasInCurrentEditableAw = approvalWorkflowAssets?.some(
        (awAsset) => awAsset.id === item.asset.id
      );
      const hasInModalAssetVersion = modalAssetVersions?.some(
        (awAsset) => awAsset.id === item.asset.id
      );
      return (
        <AssetRow
          key={item.asset.id}
          isSelected={isSelected}
          canSelectAsset={canSelectAsset}
          hasActiveAW={
            hasInCurrentEditableAw || hasInModalAssetVersion
              ? false
              : !!item.approvalWorkflowId
          }
          onToggleSelectAsset={onToggleSelectAsset}
          user={item.user}
          asset={item.asset}
          modifiedAt={item.modifiedAt}
          createdAt={item.createdAt}
        />
      );
    });
  };

  return (
    <div className="all-assets">
      <Tooltip
        title={`All media ${companyName ? `from ${companyName} project` : ''}`}
        placement="topLeft"
        overlayClassName="approval-modal-tooltip"
        className="media-step_title"
      >
        All media {companyName ? `from ${companyName} project` : ''}
      </Tooltip>
      <Input
        prefix={<SearchSvg />}
        placeholder="Search for media to add"
        size="large"
        onChange={(e) => setAssetSearchText(e.target.value)}
      />
      {isEmpty && (
        <div className="all-assets_empty-state">
          <LottieComponent className="icon" view="noSearchingResult" />
          <strong className="main-body-text main-body-text--bold">
            No results found
          </strong>
          <p className="main-body-text main-body-text--dark-gray">
            It seems we can’t find any results based on your search
          </p>
        </div>
      )}
      {!isEmpty && (
        <div className="all-assets_wrapper">
          <div className="all-assets_content">
            <div className="all-assets_inner">
              <UpgradePlanTooltip
                text=" to a Enterprise plan to access this feature"
                placement="top"
                isCanVisible={!canUseBatchApprovals}
              >
                <div style={{ display: 'inline-flex' }}>
                  <button
                    onClick={onToggleSelectAllAssets}
                    className="all-assets_btn"
                    type="button"
                    disabled={!canUseBatchApprovals}
                  >
                    {isAllSelectedAssets ? 'De-select all' : 'Select all'}
                  </button>
                </div>
              </UpgradePlanTooltip>

              {!!foldersArr.length && (
                <Collapse
                  expandIconPosition="end"
                  defaultActiveKey={defaultOpenFolders}
                  expandIcon={({ isActive }) => (
                    <DownArrowSvg
                      style={{
                        transition: 'transform 0.3s',
                        transform: `
                          translateY(-50%) rotate(${isActive ? -180 : 0}deg)
                        `
                      }}
                    />
                  )}
                >
                  {foldersArr.map(([folderName, folderAssets], index) => {
                    const assetVersionsWithoutAw = folderAssets.filter(
                      (assetVersion) => !assetVersion.approvalWorkflowId
                    );
                    const isSelectedAllAssets = assetVersionsWithoutAw.every(
                      (assetVersion) =>
                        selectedAssetVersions.some(
                          (i) => i.id === assetVersion.asset.id
                        )
                    );
                    // when you can select just one file and show that it is in this folder
                    const isSelectedOneAsset =
                      !canUseBatchApprovals &&
                      assetVersionsWithoutAw.some((assetVersion) =>
                        selectedAssetVersions.some(
                          (i) => i.id === assetVersion.asset.id
                        )
                      );
                    const isAllAssetsHasActiveAw = folderAssets.every(
                      (assetVersion) => !!assetVersion.approvalWorkflowId
                    );
                    const isChecked = isSelectedOneAsset || isSelectedAllAssets;
                    return (
                      <Collapse.Panel
                        className="all-assets_folder"
                        header={
                          <div className="all-assets_folder-head">
                            <UpgradePlanTooltip
                              text=" to a Enterprise plan to access this feature"
                              placement="top"
                              isCanVisible={!canUseBatchApprovals && !isChecked}
                            >
                              <Checkbox
                                className="create-approval_checkbox"
                                onClick={(e) => e.stopPropagation()}
                                checked={isChecked && !isAllAssetsHasActiveAw}
                                disabled={
                                  isAllAssetsHasActiveAw ||
                                  (!canUseBatchApprovals && !isChecked)
                                }
                                onChange={() =>
                                  isChecked
                                    ? onDeleteSelectFolder(folderAssets)
                                    : onSelectFolder(folderAssets)
                                }
                              />
                            </UpgradePlanTooltip>

                            <FolderSvg />
                            {folderName}
                          </div>
                        }
                        key={`${folderName}-${index}`}
                      >
                        {renderAssetRows(folderAssets)}
                      </Collapse.Panel>
                    );
                  })}
                </Collapse>
              )}
              {!!assets.length && renderAssetRows(assets)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
